import React from "react";
import { Box, Typography, Paper, Chip } from "@mui/material";
import {
  HourglassEmpty as HourglassIcon,
  CalendarToday as CalendarIcon,
  InfoOutlined as InfoIcon,
} from "@mui/icons-material";
import { formatDateTime, extractDomains } from "./helpers";

export default function PendingRequestView({ pendingRequest }) {
  if (!pendingRequest) return null;

  const reqTrainer = pendingRequest.requested_trainer || {};
  const prevTrainer = pendingRequest.previous_trainer || null;
  const reqDomainInfo = extractDomains(reqTrainer.domains);
  const prevDomainInfo = prevTrainer ? extractDomains(prevTrainer.domains) : null;
  const { date: reqDate, time: reqTime } = formatDateTime(pendingRequest.created_at);
  const requestId = pendingRequest.request_id || pendingRequest.id;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      {/* Pending Status Alert Banner */}
      <Box
        sx={{
          backgroundColor: "#FFFBEB",
          border: "1px solid #FEF3C7",
          borderRadius: "12px",
          p: 2,
          display: "flex",
          alignItems: "flex-start",
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            backgroundColor: "#F59E0B",
            color: "#FFFFFF",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            mt: 0.2,
          }}
        >
          <HourglassIcon sx={{ fontSize: 18 }} />
        </Box>
        <Box>
          <Typography
            sx={{
              fontFamily: '"Poppins", sans-serif',
              fontSize: "14px",
              fontWeight: 700,
              color: "#92400E",
            }}
          >
            Trainer Change Request Pending Review
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Poppins", sans-serif',
              fontSize: "12.5px",
              color: "#B45309",
              mt: 0.25,
              lineHeight: 1.45,
            }}
          >
            You have already submitted a trainer change request. It is currently under review by the EduSkills coordinator.
          </Typography>
        </Box>
      </Box>

      {/* Requested / Previous Trainer Details Card */}
      <Paper
        elevation={0}
        sx={{
          border: "1.5px solid #DBEAFE",
          borderRadius: "14px",
          p: 2.5,
          backgroundColor: "#FFFFFF",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          boxShadow: "0 2px 10px rgba(37, 99, 235, 0.05)",
        }}
      >
        {/* Status Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
            pb: 1.5,
            borderBottom: "1px solid #F1F5F9",
          }}
        >
          <Typography
            sx={{
              fontFamily: '"Poppins", sans-serif',
              fontSize: "13.5px",
              fontWeight: 700,
              color: "#0F172A",
            }}
          >
            Request Details
          </Typography>

          <Chip
            icon={<HourglassIcon sx={{ fontSize: 14, color: "#D97706" }} />}
            label="Pending Review"
            size="small"
            sx={{
              fontFamily: '"Poppins", sans-serif',
              backgroundColor: "#FFFBEB",
              color: "#D97706",
              border: "1px solid #FEF3C7",
              fontSize: "12px",
              fontWeight: 600,
              height: "26px",
              borderRadius: "13px",
              px: 1,
              "& .MuiChip-icon": { ml: 0.5 },
            }}
          />
        </Box>

        {/* Trainers Grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: prevTrainer ? { xs: "1fr", sm: "1fr 1fr" } : "1fr",
            gap: 1.5,
          }}
        >
          {/* Previous Trainer (if available) */}
          {prevTrainer && (
            <Box
              sx={{
                backgroundColor: "#F8FAFC",
                border: "1px solid #E2E8F0",
                borderRadius: "10px",
                p: 1.75,
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography
                  sx={{
                    fontFamily: '"Poppins", sans-serif',
                    fontSize: "11px",
                    fontWeight: 600,
                    color: "#64748B",
                    textTransform: "uppercase",
                    letterSpacing: "0.4px",
                  }}
                >
                  Previously Assigned
                </Typography>
                <Chip
                  label="Current / Previous"
                  size="small"
                  sx={{
                    fontFamily: '"Poppins", sans-serif',
                    backgroundColor: "#F1F5F9",
                    color: "#475569",
                    fontSize: "10px",
                    fontWeight: 600,
                    height: "18px",
                  }}
                />
              </Box>

              <Typography
                sx={{
                  fontFamily: '"Poppins", sans-serif',
                  fontSize: "14.5px",
                  fontWeight: 700,
                  color: "#334155",
                }}
              >
                {prevTrainer.trainer_name || "—"}
              </Typography>

              {prevDomainInfo && prevDomainInfo.all.length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, pt: 0.5 }}>
                  {prevDomainInfo.fdp.map((d, di) => (
                    <Chip
                      key={`p-fdp-${di}`}
                      label={`FDP: ${d}`}
                      size="small"
                      sx={{
                        fontFamily: '"Poppins", sans-serif',
                        backgroundColor: "#F1F5F9",
                        color: "#475569",
                        border: "1px solid #E2E8F0",
                        fontSize: "11px",
                        fontWeight: 500,
                        height: "22px",
                        borderRadius: "5px",
                      }}
                    />
                  ))}
                  {prevDomainInfo.techcamp.map((d, di) => (
                    <Chip
                      key={`p-tc-${di}`}
                      label={`TechCamp: ${d}`}
                      size="small"
                      sx={{
                        fontFamily: '"Poppins", sans-serif',
                        backgroundColor: "#F1F5F9",
                        color: "#475569",
                        border: "1px solid #E2E8F0",
                        fontSize: "11px",
                        fontWeight: 500,
                        height: "22px",
                        borderRadius: "5px",
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          )}

          {/* Requested Trainer */}
          <Box
            sx={{
              backgroundColor: prevTrainer ? "#EFF6FF" : "#F8FAFC",
              border: prevTrainer ? "1px solid #BFDBFE" : "1px solid #DBEAFE",
              borderRadius: "10px",
              p: 1.75,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Typography
                sx={{
                  fontFamily: '"Poppins", sans-serif',
                  fontSize: "11px",
                  fontWeight: 600,
                  color: prevTrainer ? "#2563EB" : "#64748B",
                  textTransform: "uppercase",
                  letterSpacing: "0.4px",
                }}
              >
                Requested Trainer
              </Typography>
              <Chip
                label="Requested"
                size="small"
                sx={{
                  fontFamily: '"Poppins", sans-serif',
                  backgroundColor: prevTrainer ? "#DBEAFE" : "#EFF6FF",
                  color: "#1E40AF",
                  fontSize: "10px",
                  fontWeight: 600,
                  height: "18px",
                }}
              />
            </Box>

            <Typography
              sx={{
                fontFamily: '"Poppins", sans-serif',
                fontSize: "14.5px",
                fontWeight: 700,
                color: "#0F172A",
              }}
            >
              {reqTrainer.trainer_name || "Trainer"}
            </Typography>

            {reqDomainInfo.all.length > 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, pt: 0.5 }}>
                {reqDomainInfo.fdp.map((d, di) => (
                  <Chip
                    key={`r-fdp-${di}`}
                    label={`FDP: ${d}`}
                    size="small"
                    sx={{
                      fontFamily: '"Poppins", sans-serif',
                      backgroundColor: "#EFF6FF",
                      color: "#1E40AF",
                      border: "1px solid #DBEAFE",
                      fontSize: "11px",
                      fontWeight: 500,
                      height: "22px",
                      borderRadius: "5px",
                    }}
                  />
                ))}
                {reqDomainInfo.techcamp.map((d, di) => (
                  <Chip
                    key={`r-tc-${di}`}
                    label={`TechCamp: ${d}`}
                    size="small"
                    sx={{
                      fontFamily: '"Poppins", sans-serif',
                      backgroundColor: "#FAF5FF",
                      color: "#6B21A8",
                      border: "1px solid #F3E8FF",
                      fontSize: "11px",
                      fontWeight: 500,
                      height: "22px",
                      borderRadius: "5px",
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
        </Box>

        {/* Request Metadata Grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 1.5,
            pt: 1.5,
            borderTop: "1px solid #F1F5F9",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CalendarIcon sx={{ fontSize: 18, color: "#2563EB" }} />
            <Box>
              <Typography
                sx={{
                  fontFamily: '"Poppins", sans-serif',
                  fontSize: "11px",
                  color: "#64748B",
                  fontWeight: 500,
                }}
              >
                Request Date &amp; Time
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"Poppins", sans-serif',
                  fontSize: "12.5px",
                  fontWeight: 600,
                  color: "#1E293B",
                }}
              >
                {reqDate} {reqTime ? `, ${reqTime}` : ""}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <InfoIcon sx={{ fontSize: 18, color: "#2563EB" }} />
            <Box>
              <Typography
                sx={{
                  fontFamily: '"Poppins", sans-serif',
                  fontSize: "11px",
                  color: "#64748B",
                  fontWeight: 500,
                }}
              >
                Current Status
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"Poppins", sans-serif',
                  fontSize: "12.5px",
                  fontWeight: 600,
                  color: "#D97706",
                }}
              >
                Pending Review
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Remark */}
        {pendingRequest.remark && (
          <Box
            sx={{
              backgroundColor: "#F8FAFC",
              border: "1px solid #E2E8F0",
              borderRadius: "8px",
              p: 1.5,
            }}
          >
            <Typography
              sx={{
                fontFamily: '"Poppins", sans-serif',
                fontSize: "11.5px",
                fontWeight: 600,
                color: "#64748B",
                mb: 0.25,
              }}
            >
              Your Remark:
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Poppins", sans-serif',
                fontSize: "12.5px",
                color: "#334155",
                fontStyle: "italic",
              }}
            >
              "{pendingRequest.remark}"
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Explanatory Notice */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          p: 1.5,
          backgroundColor: "#F8FAFC",
          borderRadius: "10px",
          border: "1px solid #E2E8F0",
        }}
      >
        <InfoIcon sx={{ fontSize: 20, color: "#64748B", flexShrink: 0 }} />
        <Typography
          sx={{
            fontFamily: '"Poppins", sans-serif',
            fontSize: "12.5px",
            color: "#64748B",
            lineHeight: 1.45,
          }}
        >
          You cannot submit a new trainer change request while another request is pending review. Once the coordinator takes action, you will be notified.
        </Typography>
      </Box>
    </Box>
  );
}
